import { DateTime } from 'luxon';

export function resolvedLocale(language: string): string {
  const requested = [language, language.split('-')[0]].filter(Boolean);
  const supported = Intl.DateTimeFormat.supportedLocalesOf(requested);
  if (supported.length > 0) {
    return supported[0];
  }
  return Intl.DateTimeFormat().resolvedOptions().locale;
}

function timeZoneName(
  zone: string,
  at: Date,
  locale: string,
  name: 'short' | 'shortOffset'
): string | undefined {
  return new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    timeZoneName: name,
  })
    .formatToParts(at)
    .find(part => part.type === 'timeZoneName')?.value;
}

export function formatTimezoneWithDst(
  zone: string,
  at: DateTime = DateTime.now(),
  locale = resolvedLocale(Intl.DateTimeFormat().resolvedOptions().locale)
): string {
  const zoned = at.setZone(zone);
  if (!zoned.isValid) {
    return zone;
  }
  const instant = zoned.toJSDate();
  const shortName = timeZoneName(zone, instant, locale, 'short');
  const offset = timeZoneName(zone, instant, locale, 'shortOffset');
  if (shortName && offset && shortName !== offset) {
    return `${zone} (${shortName}, ${offset})`;
  }
  if (offset) {
    return `${zone} (${offset})`;
  }
  if (shortName) {
    return `${zone} (${shortName})`;
  }
  return zone;
}

export function zonedDateAndTime(
  zone: string,
  at: DateTime,
  locale: string
): { date: string; time: string; hour: number } {
  const zoned = at.setZone(zone).setLocale(locale);
  return {
    date: zoned.toLocaleString({
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }),
    time: zoned.toFormat('HH:mm'),
    hour: zoned.hour,
  };
}
